import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import type { EnqueueSnackbar } from 'notistack';
import { shallow } from 'zustand/shallow';
import { Button } from '@components/button';
import { ToastType } from '@components/toast';
import { useServiceWorkerUpdateStore } from '@store/ServiceWorkerUpdateStore';

const UPDATE_TOAST_KEY = 'new-version-toast';

const shouldShowUpdateToast = () => {
  const { activeWalkthroughKey, needRefresh } = useServiceWorkerUpdateStore.getState();
  return needRefresh && !activeWalkthroughKey;
};

function enqueueUpdateToast(enqueueSnackbar: EnqueueSnackbar) {
  enqueueSnackbar(
    'Important: Application has a new version available. Please update to ensure full functionality of the application.',
    {
      key: UPDATE_TOAST_KEY,
      persist: true,
      preventDuplicate: true,
      variant: ToastType.SiteUpdateAlert,
      action: <UpdateToastAction />,
      onExited: () => {
        // notistack removes the exited toast after this callback runs, so wait a tick before re-enqueueing
        setTimeout(() => {
          if (shouldShowUpdateToast()) enqueueUpdateToast(enqueueSnackbar);
        });
      },
    },
  );
}

const UpdateToastAction = () => {
  const { applyUpdate, isUpdating } = useServiceWorkerUpdateStore(
    (state) => ({
      applyUpdate: state.applyUpdate,
      isUpdating: state.isUpdating,
    }),
    shallow,
  );

  return (
    <Button
      id='update-now-toast-button'
      variant='text'
      disabled={isUpdating}
      sx={{
        color: 'white !important',
        '&:hover': {
          textDecoration: 'underline !important',
        },
      }}
      onClick={() => {
        void applyUpdate();
      }}
    >
      {isUpdating ? 'Updating...' : 'Get New Version'}
    </Button>
  );
};

/**
 * Persistent bottom-left toast shown alongside the header banner when a new service worker is waiting.
 * Pages call closeSnackbar() with no key on navigation, so the toast re-opens itself on exit while an
 * update is still pending.
 */
export const ServiceWorkerUpdateToast = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const showToast = useServiceWorkerUpdateStore((state) => state.needRefresh && !state.activeWalkthroughKey);

  useEffect(() => {
    if (showToast) {
      enqueueUpdateToast(enqueueSnackbar);
    } else {
      closeSnackbar(UPDATE_TOAST_KEY);
    }
  }, [showToast, enqueueSnackbar, closeSnackbar]);

  return null;
};
